import * as React from 'react';

import { SiApple, SiLinux } from '@icons-pack/react-simple-icons';
import {
  enumSidecarOperatingSystem,
  enumSidecarStatus,
} from '@rematter/frontend-lib-api-client';
import {
  SidecarRecord,
  type SidecarOperatingSystem,
  type SidecarDisplayStatus,
} from '@rematter/modules/settings/types/sidecar';
import {
  Badge,
  Col,
  Row,
  SearchField,
  ThemeColor,
  TinyTable,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import {
  LucideCog,
  LucideCpu,
  LucideGlobe,
  LucideMapPin,
  LucideMonitor,
  LucideRadioTower,
} from 'lucide-react';

import { useSidecarEventsSubscription } from '../../../_graphql/subscriptionSidecarEvents';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import * as state from '../../../_state/sidecarsList.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { WindowsIcon } from '~/shared/components/icons';
import { useLibp2p } from '~/shared/providers/libp2p';
import { useHardwarePresenceMembers } from '~/shared/providers/presence';

const STATUS_COLORS: Record<SidecarDisplayStatus, ThemeColor> = {
  Pending: 'warning',
  Online: 'success',
  Offline: 'neutral',
};

export const SidecarsListView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const [searchTerm, setSearchTerm] = React.useState('');
  const { connections } = useLibp2p();
  const presenceMembers = useHardwarePresenceMembers();

  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteSidecars,
  );

  useSidecarEventsSubscription(undefined, {
    onEvent: () => {
      refetch();
    },
  });

  const allSidecars = data?.sidecars ?? [];

  const sidecars = React.useMemo(() => {
    if (!searchTerm.trim()) return allSidecars;
    const term = searchTerm.toLowerCase();
    return allSidecars.filter(
      (s) =>
        s.name.toLowerCase().includes(term) ||
        s.operatingSystem.toLowerCase().includes(term) ||
        s.facility?.name.toLowerCase().includes(term) ||
        s.localIpAddress?.toLowerCase().includes(term),
    );
  }, [allSidecars, searchTerm]);

  const osIcon = (os: SidecarOperatingSystem) => {
    const cls = 'size-4 shrink-0';
    switch (os) {
      case enumSidecarOperatingSystem.Windows:
        return <WindowsIcon className={cls} />;
      case enumSidecarOperatingSystem.Linux:
        return <SiLinux className={cls} />;
      case enumSidecarOperatingSystem.MacOS:
        return <SiApple className={cls} />;
      case enumSidecarOperatingSystem.Other:
        return <LucideCog className={cls} />;
    }
  };

  return (
    <Col gap4 className="@SidecarsListView h-full p-4">
      <Row gap2>
        <SearchField
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t.hardwareView.sidecarsPanel.search.placeholder}
          aria-label={t.hardwareView.sidecarsPanel.search.ariaLabel}
          className="w-[200px] shrink-0"
        />
      </Row>
      <TinyTable<SidecarRecord>
        rowKey={(row) => row.id}
        isLoading={loadingState.isInitialLoading || isFetching}
        error={error}
        onRowClick={(row) => setLocation(`/hardware/sidecars/${row.id}`)}
        columns={[
          {
            key: 'name',
            label: t.hardwareView.sidecarsPanel.columns.name,
            icon: LucideCpu,
            width: 'auto',
            cell: (row) => (
              <div className="truncate font-medium">{row.name}</div>
            ),
          },
          {
            key: 'status',
            label: t.hardwareView.sidecarsPanel.columns.status,
            icon: LucideRadioTower,
            width: 'max-content',
            cell: (row) => {
              const displayStatus: SidecarDisplayStatus =
                row.status === enumSidecarStatus.Pending
                  ? 'Pending'
                  : presenceMembers.has(row.id)
                    ? 'Online'
                    : 'Offline';
              const conn = connections.get(row.id);
              return (
                <Row gap1 className="items-center">
                  <Badge color={STATUS_COLORS[displayStatus]}>
                    {t.hardwareView.sidecarsPanel.statusLabels[displayStatus]}
                  </Badge>
                  {conn?.connected && (
                    <Badge
                      color={conn.type === 'direct' ? 'primary' : 'neutral'}
                    >
                      {conn.type === 'direct'
                        ? t.hardwareView.sidecarsPanel.connectionLabels.local
                        : t.hardwareView.sidecarsPanel.connectionLabels.relayed}
                    </Badge>
                  )}
                </Row>
              );
            },
          },
          {
            key: 'operatingSystem',
            label: t.hardwareView.sidecarsPanel.columns.operatingSystem,
            icon: LucideMonitor,
            width: 200,
            cell: (row) => (
              <div className="flex min-w-0 items-center gap-1.5">
                {osIcon(row.operatingSystem)}
                <span className="truncate">{row.operatingSystemVersion}</span>
              </div>
            ),
          },
          {
            key: 'facility',
            label: t.hardwareView.sidecarsPanel.columns.facility,
            icon: LucideMapPin,
            width: 180,
            cell: (row) => (
              <div className="truncate">{row.facility?.name ?? '–'}</div>
            ),
          },
          {
            key: 'localIpAddress',
            label: t.hardwareView.sidecarsPanel.columns.localIpAddress,
            icon: LucideGlobe,
            width: 150,
            cell: (row) => (
              <span className="font-mono text-sm">
                {row.localIpAddress ?? '–'}
              </span>
            ),
          },
        ]}
        data={sidecars}
      />
    </Col>
  );
};
